import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import {
  AzTableComponent,
  AzTableColumn,
  AzTableAction,
} from '../../shared/components/az-table/az-table.component';
import { AzButtonComponent } from '../../shared/components/az-button/az-button.component';
import { AzDocumentUpdateModalComponent } from '../../shared/components/az-document-update-modal/az-document-update-modal.component';
import { StatusChangeModalComponent } from '../../shared/components/status-change-modal/status-change-modal.component';
import { DocumentRepositoryService } from './services/document-repository.service';
import {
  DocumentEntry,
  ContactsApiResponse,
  RecipientContact,
} from './models/document-entry.model';
import { LoggerService } from '../../core/logging/logger.service';
import { UserPermissionsService } from '../../core/auth/services/user-permissions.service';
import { HttpEventType } from '@angular/common/http';

export interface DocumentDisplayRow {
  name: string;
  uploadDate: string;
  status: string;
  uploadedBy: string;
}

@Component({
  selector: 'app-document-repository',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ToolbarComponent,
    FooterComponent,
    AzTableComponent,
    AzButtonComponent,
    AzDocumentUpdateModalComponent,
    StatusChangeModalComponent,
  ],
  providers: [DatePipe],
  templateUrl: './document-repository.component.html',
  styleUrl: './document-repository.component.scss',
})
export class DocumentRepositoryComponent implements OnInit {
  private datePipe = inject(DatePipe);

  @ViewChild('uploadStatusModal') statusModal!: StatusChangeModalComponent;
  @ViewChild(AzDocumentUpdateModalComponent) documentUpdateModal!: AzDocumentUpdateModalComponent;

  loading = signal(false);
  loadError = signal<string | null>(null);
  isStatusModalOpen = signal(false);
  isPendingWarningOpen = signal(false);
  isLoadingContacts = signal(false);
  statusModalTitle = signal('Document Repository');
  statusModalProcessingTitle = signal('Loading...');
  documents: DocumentEntry[] = [];
  showUploadModal = signal(false);

  // BCR-173: notification flow state
  pendingReplacementData = signal<{
    file: File | null;
    documentType: string;
    comments: string;
  } | null>(null);
  contacts = signal<ContactsApiResponse | null>(null);
  contactsError = signal('');

  readonly documentTypes = [
    'Binding Corporate Rules EU',
    'Binding Corporate Rules UK',
    'Data Privacy Standard EU',
    'Data Privacy Standard UK',
  ];

  columns: AzTableColumn[] = [
    { key: 'name', label: 'Document' },
    { key: 'uploadedBy', label: 'Uploaded By' },
    { key: 'uploadDate', label: 'Upload Date' },
    { key: 'status', label: 'Status', type: 'tag', transform: 'capitalize' },
  ];

  actions: AzTableAction<DocumentDisplayRow>[] = [
    {
      icon: 'download',
      label: 'Download',
      callback: (row: DocumentDisplayRow) => this.onDownload(row),
    },
  ];

  get displayDocuments(): DocumentDisplayRow[] {
    return this.documents.map((doc) => ({
      name: doc.name,
      uploadDate: this.datePipe.transform(doc.uploadDate, 'dd/MMM/yyyy', 'UTC') ?? '',
      status: doc.status,
      uploadedBy: doc.uploadedBy || '--',
    }));
  }

  get canReplaceFile(): boolean {
    return (
      this.userPermissions.hasDocumentRepositoryUploadPermission() &&
      this.userPermissions.hasPstMemberRole()
    );
  }

  readonly userPermissionsService = inject(UserPermissionsService);
  private router = inject(Router);

  toSlug(documentName: string): string {
    return documentName.toLowerCase().replace(/ /g, '-');
  }

  onDocumentRowClick(row: DocumentDisplayRow): void {
    if (this.userPermissionsService.hasPstMemberRole()) {
      this.router.navigate(['/document-repository', this.toSlug(row.name), 'contacts']);
    }
  }

  constructor(
    private documentRepositoryService: DocumentRepositoryService,
    private logger: LoggerService,
    private userPermissions: UserPermissionsService,
  ) {}

  ngOnInit(): void {
    this.logger.info('Document Repository page loaded');
    this.loadDocuments();
  }

  openUploadModal(): void {
    this.showUploadModal.set(true);
  }

  closeUploadModal(): void {
    this.showUploadModal.set(false);
    // Reset notification flow state when modal is closed
    this.pendingReplacementData.set(null);
    this.contacts.set(null);
    this.contactsError.set('');
    this.isLoadingContacts.set(false);
  }

  closeStatusModal(): void {
    this.isStatusModalOpen.set(false);
    if (this.documentUpdateModal) {
      this.documentUpdateModal.resetForm();
    }
  }

  onDownload(row: DocumentDisplayRow): void {
    this.statusModalTitle.set('Download Document');
    this.statusModalProcessingTitle.set('Generating download link...');
    this.isStatusModalOpen.set(true);

    this.documentRepositoryService.getDownloadUrl(row.name).subscribe({
      next: (res) => {
        this.isStatusModalOpen.set(false);
        window.open(res.download_url, '_blank');
      },
      error: (err) => {
        this.logger.error('Failed to get download URL', err);
        const message = err?.error?.message || err?.message || 'Failed to generate download URL.';
        this.statusModal.showError(message, 'Download Failed', 'error');
      },
    });
  }

  // Step 1 confirmed — retain its data for the contract and contact-selection steps.
  onStepOneConfirmed(data: { file: File | null; documentType: string; comments: string }): void {
    const selectedDoc = this.documents.find((doc) => doc.name === data.documentType);
    if (selectedDoc?.status?.toLowerCase() === 'pending') {
      // Store data but wait for user to confirm before proceeding
      this.pendingReplacementData.set(data);
      this.contacts.set(null);
      this.contactsError.set('');
      this.isPendingWarningOpen.set(true);
      return;
    }
    this.pendingReplacementData.set(data);
    this.contacts.set(null);
    this.contactsError.set('');
  }

  onPendingWarningNo(): void {
    this.isPendingWarningOpen.set(false);
    this.closeUploadModal();
  }

  onPendingWarningYes(): void {
    this.isPendingWarningOpen.set(false);
    // pendingReplacementData is already set; step 2 is already visible — flow continues normally
  }

  onContactsRequested(): void {
    const documentType = this.pendingReplacementData()?.documentType;
    if (!documentType) return;

    if (this.contacts()) {
      return;
    }

    this.contactsError.set('');
    this.isLoadingContacts.set(true);

    this.documentRepositoryService.getContacts(documentType).subscribe({
      next: (contacts) => {
        this.isLoadingContacts.set(false);
        this.contacts.set(contacts);
      },
      error: (err) => {
        this.isLoadingContacts.set(false);
        this.contactsError.set('Unable to load contacts. Please try again.');
        this.logger.error('Failed to load contacts for selection', err);
      },
    });
  }

  // BCR-253 follow-up: the backend accepts an array of {email, legal_entity_id}
  // recipient objects so it can disambiguate which specific Legal Entity row(s)
  // were selected for a contact whose email appears against multiple entities.
  // legal_entity_id is not yet returned by GET /contacts; it will be `null` for
  // every recipient (including IGA Coordinators) until the backend adds it.
  onNotifyConfirmed(payload: {
    replacementFile: File;
    documentType: string;
    adobePdf: File;
    selectedContacts: RecipientContact[];
  }): void {
    this.closeUploadModal();
    this.statusModalTitle.set('Document Repository');
    this.statusModalProcessingTitle.set('Uploading documents and sending notifications...');
    this.isStatusModalOpen.set(true);

    // Step 1: Get dual presigned URLs (file + contract in one request)
    this.documentRepositoryService.getPresignedUrl().subscribe({
      next: (presigned) => {
        // Step 2: Upload replacement file to S3
        this.documentRepositoryService
          .uploadToS3(payload.replacementFile, presigned.file.presigned_post)
          .subscribe({
            next: (fileEvent) => {
              if (fileEvent.type !== HttpEventType.Response) return;
              // Step 3: Upload Adobe PDF (contract) to S3
              this.documentRepositoryService
                .uploadToS3(payload.adobePdf, presigned.contract.presigned_post)
                .subscribe({
                  next: (contractEvent) => {
                    if (contractEvent.type !== HttpEventType.Response) return;
                    // Step 4: Confirm upload — backend handles agreement creation + notifications
                    this.documentRepositoryService
                      .confirmUpload({
                        document_type: payload.documentType,
                        file_s3_key: presigned.file.s3_key,
                        contract_s3_key: presigned.contract.s3_key,
                        selected_recipients: payload.selectedContacts
                          .filter(
                            (contact): contact is RecipientContact & { email: string } =>
                              !!contact.email,
                          )
                          .map((contact) => ({
                            email: contact.email,
                            legal_entity_id: contact.legal_entity_id ?? null,
                          })),
                      })
                      .subscribe({
                        next: () => {
                          this.loadDocuments();
                          this.statusModal.showSuccessWithMessage(
                            'Document replacement complete. Signing notifications sent successfully.',
                            'Replace File',
                          );
                        },
                        error: (err) => {
                          const msg =
                            err?.error?.message || err?.message || 'Failed to confirm upload.';
                          this.statusModal.showError(msg, 'Upload Failed', 'error');
                          this.logger.error('BCR-173 confirm-upload failed', err);
                        },
                      });
                  },
                  error: (err) => {
                    this.statusModal.showError(
                      'Failed to upload signing document.',
                      'Upload Failed',
                      'error',
                    );
                    this.logger.error('BCR-173 contract S3 upload failed', err);
                  },
                });
            },
            error: (err) => {
              this.statusModal.showError(
                'Failed to upload replacement file to storage.',
                'Upload Failed',
                'error',
              );
              this.logger.error('BCR-173 replacement S3 upload failed', err);
            },
          });
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Failed to initiate upload.';
        this.statusModal.showError(msg, 'Upload Failed', 'error');
        this.logger.error('BCR-173 presigned-url failed', err);
      },
    });
  }

  private loadDocuments(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.documentRepositoryService.getDocuments().subscribe({
      next: (docs) => {
        this.documents = docs;
        this.loading.set(false);
      },
      error: (err) => {
        this.logger.error('Failed to load document repository', err);
        this.loadError.set('Failed to load documents. Please try again.');
        this.loading.set(false);
      },
    });
  }
}
